import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { DataTablesModule } from 'angular-datatables';
import { ClipboardModule } from 'ngx-clipboard';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { appRouting } from './app.router';
import { ActivateProfileComponent } from './components/activate-profile/activate-profile.component';
import { AdminContentPageEditModalComponent } from './components/admin/admin-content/admin-content-pages/admin-content-page-edit-modal/admin-content-page-edit-modal.component';
import { AdminContentPagesComponent } from './components/admin/admin-content/admin-content-pages/admin-content-pages.component';
import { AdminContentResourceEditModalComponent } from './components/admin/admin-content/admin-content-resources/admin-content-resource-edit-modal/admin-content-resource-edit-modal.component';
import { AdminContentResourcesComponent } from './components/admin/admin-content/admin-content-resources/admin-content-resources.component';
import { AdminContentComponent } from './components/admin/admin-content/admin-content.component';
import { AdminDashboardComponent } from './components/admin/admin-dashboard/admin-dashboard.component';
import { AdminEventsComponent } from './components/admin/admin-events/admin-events.component';
import { AdminInstallerJobsComponent } from './components/admin/admin-installer-jobs/admin-installer-jobs.component';
import { AdminPro3Component } from './components/admin/admin-pro3/admin-pro3.component';
import { AdminPromotionsCreateModalComponent } from './components/admin/admin-rewards/admin-promotions-create-modal/admin-promotions-create-modal.component';
import { AdminPromotionsViewModalComponent } from './components/admin/admin-rewards/admin-promotions-view-modal/admin-promotions-view-modal.component';
import { AdminRewardsEditModalComponent } from './components/admin/admin-rewards/admin-rewards-edit-modal/admin-rewards-edit-modal.component';
import { AdminRewardsComponent } from './components/admin/admin-rewards/admin-rewards.component';
import { AdminUserPointsReportModalComponent } from './components/admin/admin-rewards/admin-user-points-report-modal/admin-user-points-report-modal.component';
import { AdminUserEditModalComponent } from './components/admin/admin-users/admin-user-edit-modal/admin-user-edit-modal.component';
import { AdminUsersComponent } from './components/admin/admin-users/admin-users.component';
import { BusinessToolsComponent } from './components/business-tools/business-tools.component';
import { TradehelpComponent } from './components/business-tools/tradehelp/tradehelp.component';
import { TruequoteWebsiteComponent } from './components/business-tools/truequote-website/truequote-website.component';
import { TruequoteComponent } from './components/business-tools/truequote/truequote.component';
import { ConfirmPromptComponent } from './components/common/confirm-prompt/confirm-prompt.component';
import { FileDownloadComponent } from './components/common/file-download/file-download.component';
import { FileUploadComponent } from './components/common/file-upload/file-upload.component';
import { GenericModalComponent } from './components/common/generic-modal/generic-modal.component';
import { MessagePanelComponent } from './components/common/message-panel/message-panel.component';
import { PageContentComponent } from './components/common/page-content/page-content.component';
import { PostcodeLookupModalComponent } from './components/common/postcode-lookup-modal/postcode-lookup-modal.component';
import { ProcheckRedirectComponent } from './components/common/procheck-redirect/procheck-redirect.component';
import { PromotionModalComponent } from './components/common/promotion-modal/promotion-modal.component';
import { RichEditorComponent } from './components/common/rich-editor/rich-editor.component';
import { VideoComponent } from './components/common/video-component/video.component';
import { YoutubeModalComponent } from './components/common/youtube-modal/youtube-modal.component';
import { ContactUsComponent } from './components/contact-us/contact-us.component';
import { ContentArchiveComponent } from './components/content-archive/content-archive.component';
import { ContentItemComponent } from './components/content-archive/content-item/content-item.component';
import { ContentListComponent } from './components/content-archive/content-list/content-list.component';
import { DiaryCalendarComponent } from './components/diary/diary-calendar/diary-calendar.component';
import { DiaryEventComponent } from './components/diary/diary-event/diary-event.component';
import { DiaryListEventsModalComponent } from './components/diary/diary-list-events-modal/diary-list-events-modal.component';
import { DiaryListEventsComponent } from './components/diary/diary-list-events/diary-list-events.component';
import { DiarySingleEventComponent } from './components/diary/diary-single-event/diary-single-event.component';
import { DiaryComponent } from './components/diary/diary.component';
import { FilterSliderComponent } from './components/filter-slider/filter-slider.component';
import { BusinessToolsWidgetComponent } from './components/homepage-widgets/business-tools-widget/business-tools-widget.component';
import { DiaryWidgetComponent } from './components/homepage-widgets/diary-widget/diary-widget.component';
import { ProductWidgetComponent } from './components/homepage-widgets/product-widget/product-widget.component';
import { ProfileWidgetComponent } from './components/homepage-widgets/profile-widget/profile-widget.component';
import { ResourceWidgetComponent } from './components/homepage-widgets/resource-widget/resource-widget.component';
import { RewardsWidgetComponent } from './components/homepage-widgets/rewards-widget/rewards-widget.component';
import { RoiWidgetComponent } from './components/homepage-widgets/roi-widget/roi-widget.component';
import { ServiceWidgetComponent } from './components/homepage-widgets/service-widget/service-widget.component';
import { WarrantyWidgetComponent } from './components/homepage-widgets/warranty-widget/warranty-widget.component';
import { HomepageComponent } from './components/homepage/homepage.component';
import { JobDetailsComponent } from './components/job/job-details/job-details.component';
import { JobEditModalComponent } from './components/job/job-edit-modal/job-edit-modal.component';
import { JobComponent } from './components/job/job.component';
import { PrintableJobComponent } from './components/job/printable-job/printable-job.component';
import { LoginExistingComponent } from './components/login-existing/login-existing.component';
import { LoginComponent } from './components/login/login.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { Pro3ListComponent } from './components/pro3-list/pro3-list.component';
import { ProductSelectorContainerComponent } from './components/product-selector-container/product-selector-container.component';
import { ProductSelectorComponent } from './components/product-selector-container/product-selector/product-selector.component';
import { RegisterComponent } from './components/register/register.component';
import { ResetPasswordModalComponent } from './components/reset-password-modal/reset-password-modal.component';
import { ResetPasswordResponseModalComponent } from './components/reset-password-response-modal/reset-password-response-modal.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { ReviewInvitationComponent } from './components/review-invitation/review-invitation.component';
import { DemokitRedeemComponent } from './components/rewards/demokit-redeem/demokit-redeem.component';
import { RewardsComponent } from './components/rewards/rewards.component';
import { SparesRedeemComponent } from './components/rewards/spares-redeem/spares-redeem.component';
import { VoucherRedeemComponent } from './components/rewards/voucher-redeem/voucher-redeem.component';
import { RoiCalculatorComponent } from './components/roi-calculator/roi-calculator.component';
import { InviteModalComponent } from './components/sendemail/invite-modal/invite-modal.component';
import { AddServiceRequestModalComponent } from './components/service-dashboard/add-service-request-modal/add-service-request-modal.component';
import { AddServiceRequestComponent } from './components/service-dashboard/add-service-request/add-service-request.component';
import { FlagUpdateModalComponent } from './components/service-dashboard/flag-update-modal/flag-update-modal.component';
import { ServiceDashboardComponent } from './components/service-dashboard/service-dashboard.component';
import { ServiceRecordModalComponent } from './components/service-dashboard/service-record-modal/service-record-modal.component';
import { ServiceRecordComponent } from './components/service-dashboard/service-record/service-record.component';
import { SubmissionSelectionComponent } from './components/submission-selection/submission-selection.component';
import { SystemDownComponent } from './components/system-down/system-down.component';
import { SystemMessageModalComponent } from './components/system-message-modal/system-message-modal.component';
import { TcAppLoginComponent } from './components/tc-app-login/tc-app-login.component';
import { TcPortalLoginComponent } from './components/tc-portal-login/tc-portal-login.component';
import { TrainingComponent } from './components/training/training.component';
import { OnboardingComponent } from './components/user-onboarding/onboarding/onboarding.component';
import { UserOnboardingComponent } from './components/user-onboarding/user-onboarding.component';
import { OptinComponent } from './components/user-optin/optin/optin.component';
import { UserOptinComponent } from './components/user-optin/user-optin.component';
import { ProfileDetailsComponent } from './components/user-profile/profile-details/profile-details.component';
import { ProfileEditModalComponent } from './components/user-profile/profile-edit-modal/profile-edit-modal.component';
import { ProfileHeaderComponent } from './components/user-profile/profile-header/profile-header.component';
import { ProfilePercentageComponent } from './components/user-profile/profile-percentage/profile-percentage.component';
import { ProfilePhotoUploadComponent } from './components/user-profile/profile-photo-upload/profile-photo-upload.component';
import { UserEditPasswordModalComponent } from './components/user-profile/user-edit-password-modal/user-edit-password-modal.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { WarrantyListComponent } from './components/warranty-list/warranty-list.component';
import { WarrantyStatisticsComponent } from './components/warranty-statistics/warranty-statistics.component';
import { PrintableWarrantyComponent } from './components/warranty/printable-warranty/printable-warranty.component';
import { WarrantyDetailsComponent } from './components/warranty/warranty-details/warranty-details.component';
import { WarrantyEditModalComponent } from './components/warranty/warranty-edit-modal/warranty-edit-modal.component';
import { WarrantyComponent } from './components/warranty/warranty.component';
import { ExternalLinks } from './models/main';
import { NavItems } from './models/nav-items';
import { AdminCompanyService } from './services/admin-company.service';
import { AdminInstallerService } from './services/admin-installer.service';
import { AdminService } from './services/admin.service';
import { ApiSenseService } from './services/api-sense.service';
import { ApiService } from './services/api.service';
import { BusinessToolsService } from './services/business-tools.service';
import { ContentService } from './services/content.service';
import { CountryService } from './services/country.service';
import { DiaryService } from './services/diary.service';
import { EmailService } from './services/email.service';
import { FeatureService } from './services/feature.service';
import { InvitationService } from './services/invitation.service';
import { JobService } from './services/job.service';
import { LoginService } from './services/login.service';
import { OnboardingQuestionsService } from './services/onboarding-questions.service';
import { PopupService } from './services/popup.service';
import { PostcodeLookupService } from './services/postcode-lookup.service';
import { ProductSelectorService } from './services/product-selector.service';
import { RegisterService } from './services/register.service';
import { ResourceService } from './services/resource.service';
import { RewardsService } from './services/rewards.service';
import { ServiceRecordService } from './services/service-record.service';
import { SystemMessageService } from './services/system-message.service';
import { WarrantyService } from './services/warranty.service';
import { SharedModule } from './shared/shared.module';
import { AdminEventsEffects } from './state-management/effects/admin-events.effects';
import { AdminJobsEffects } from './state-management/effects/admin-jobs.effects';
import { AdminResourcesEffects } from './state-management/effects/admin-resources.effects';
import { AdminRewardEffects } from './state-management/effects/admin-rewards.effects';
import { AdminStatsEffects } from './state-management/effects/admin-stats.effects';
import { AdminUserEffects } from './state-management/effects/admin-users.effects';
import { BusinessToolsEffects } from './state-management/effects/business-tools.effects';
import { ContentPagesEffects } from './state-management/effects/content-pages.effects';
import { CountryEffects } from './state-management/effects/country.effects';
import { CustomerSuggestionEffects } from './state-management/effects/customer-suggestion.effects';
import { DiaryEffects } from './state-management/effects/diary.effects';
import { EmailEffects } from './state-management/effects/email.effects';
import { FeatureEffects } from './state-management/effects/feature.effects';
import { InvitationEffects } from './state-management/effects/invitation.effects';
import { LoginEffects } from './state-management/effects/login.effects';
import { OnboardingQuestionEffects } from './state-management/effects/onboarding-question.effects';
import { PopupEffects } from './state-management/effects/popup.effects';
import { PostcodeLookupEffects } from './state-management/effects/postcode-lookup.effects';
import { ProductsEffects } from './state-management/effects/products.effects';
import { RegisterJobEffects } from './state-management/effects/register-job.effects';
import { RegisterWarrantyEffects } from './state-management/effects/register-warranty.effects';
import { RegistrationEffects } from './state-management/effects/registration.effects';
import { ResourceEffects } from './state-management/effects/resource.effects';
import { RewardsEffects } from './state-management/effects/rewards.effects';
import { ServiceRecordEffects } from './state-management/effects/service-record.effects';
import { SystemMessageEffects } from './state-management/effects/system-message.effects';
import { UserSuggestionEffects } from './state-management/effects/user-suggestion.effects';
import { WarrantiesEffects } from './state-management/effects/warranties.effects';
import { WarrantyStatsEffects } from './state-management/effects/warranty-stats.effects';
import { store } from './state-management/store';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Navigate to an external site
export function navigateExternal(route: ActivatedRouteSnapshot) {
  globalThis.location.href = route.data.externalUrl;
  return globalThis.location.href;
}

// Main app module definition
@NgModule({
  declarations: [
    // Top-level component
    AppComponent,
    // App components
    ActivateProfileComponent,
    AddServiceRequestComponent,
    AddServiceRequestModalComponent,
    BusinessToolsComponent,
    ConfirmPromptComponent,
    ContactUsComponent,
    ContentArchiveComponent,
    ContentItemComponent,
    ContentListComponent,
    DemokitRedeemComponent,
    DiaryCalendarComponent,
    DiaryComponent,
    DiaryEventComponent,
    DiaryListEventsComponent,
    DiaryListEventsModalComponent,
    DiarySingleEventComponent,
    FileDownloadComponent,
    FileUploadComponent,
    FlagUpdateModalComponent,
    GenericModalComponent,
    HomepageComponent,
    JobComponent,
    JobDetailsComponent,
    JobEditModalComponent,
    LoginComponent,
    LoginExistingComponent,
    MessagePanelComponent,
    PageContentComponent,
    PageNotFoundComponent,
    Pro3ListComponent,
    PostcodeLookupModalComponent,
    PrintableJobComponent,
    PrintableWarrantyComponent,
    ProductSelectorComponent,
    ProductSelectorContainerComponent,
    OptinComponent,
    OnboardingComponent,
    ProfileDetailsComponent,
    ProfileEditModalComponent,
    ProfileHeaderComponent,
    ProfilePercentageComponent,
    ProfilePhotoUploadComponent,
    PromotionModalComponent,
    RegisterComponent,
    ResetPasswordComponent,
    ResetPasswordModalComponent,
    ResetPasswordResponseModalComponent,
    RewardsComponent,
    RichEditorComponent,
    RoiCalculatorComponent,
    ServiceDashboardComponent,
    ServiceRecordComponent,
    ServiceRecordModalComponent,
    SparesRedeemComponent,
    SubmissionSelectionComponent,
    SystemDownComponent,
    TrainingComponent,
    TruequoteComponent,
    TruequoteWebsiteComponent,
    TradehelpComponent,
    UserEditPasswordModalComponent,
    UserOptinComponent,
    UserOnboardingComponent,
    UserProfileComponent,
    VideoComponent,
    VoucherRedeemComponent,
    WarrantyComponent,
    WarrantyDetailsComponent,
    WarrantyEditModalComponent,
    WarrantyListComponent,
    WarrantyStatisticsComponent,
    YoutubeModalComponent,
    // App widgets
    BusinessToolsWidgetComponent,
    DiaryWidgetComponent,
    ProductWidgetComponent,
    ProfileWidgetComponent,
    ResourceWidgetComponent,
    RewardsWidgetComponent,
    RoiWidgetComponent,
    ServiceWidgetComponent,
    WarrantyWidgetComponent,
    // Admin components
    AdminContentComponent,
    AdminContentPageEditModalComponent,
    AdminContentPagesComponent,
    AdminContentResourceEditModalComponent,
    AdminContentResourcesComponent,
    AdminDashboardComponent,
    AdminEventsComponent,
    AdminInstallerJobsComponent,
    AdminPro3Component,
    AdminPromotionsCreateModalComponent,
    AdminPromotionsViewModalComponent,
    AdminRewardsComponent,
    AdminRewardsEditModalComponent,
    AdminUserEditModalComponent,
    AdminUsersComponent,
    AdminUserPointsReportModalComponent,
    //Pipes and useful bits:
    FilterSliderComponent,
    ReviewInvitationComponent,
    InviteModalComponent,
    TcPortalLoginComponent,
    TcAppLoginComponent,
    SystemMessageModalComponent,
    ProcheckRedirectComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    // Base Angular modules
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    ServiceWorkerModule.register('/ngsw-worker.js', {
      enabled: environment.production,
    }),
    SharedModule,
    // Third-party modules
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    ClipboardModule,
    DataTablesModule,
    // Angular Material modules
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSnackBarModule,
    MatSlideToggleModule,
    MatStepperModule,
    MatTabsModule,
    MatTooltipModule,
    // Routing
    appRouting,
    // Store module
    StoreModule.forRoot(store),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      name: 'ADEY ProClub Devtools',
      connectInZone: true,
    }),
    // Translation module (ngx-translate)
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    // Effects module (containing all app effects)
    EffectsModule.forRoot([
      AdminEventsEffects,
      AdminJobsEffects,
      AdminResourcesEffects,
      AdminRewardEffects,
      AdminStatsEffects,
      AdminUserEffects,
      BusinessToolsEffects,
      ContentPagesEffects,
      CountryEffects,
      DiaryEffects,
      EmailEffects,
      FeatureEffects,
      LoginEffects,
      PopupEffects,
      PostcodeLookupEffects,
      ProductsEffects,
      RegisterJobEffects,
      RegisterWarrantyEffects,
      RegistrationEffects,
      ResourceEffects,
      RewardsEffects,
      ServiceRecordEffects,
      UserSuggestionEffects,
      CustomerSuggestionEffects,
      WarrantiesEffects,
      WarrantyStatsEffects,
      InvitationEffects,
      SystemMessageEffects,
      OnboardingQuestionEffects,
    ]),
  ],
  providers: [
    // App services
    AdminCompanyService,
    AdminInstallerService,
    AdminService,
    ApiService,
    BusinessToolsService,
    ContentService,
    CountryService,
    DiaryService,
    EmailService,
    FeatureService,
    ExternalLinks,
    JobService,
    LoginService,
    NavItems,
    PopupService,
    PostcodeLookupService,
    ProductSelectorService,
    RegisterService,
    ResourceService,
    RewardsService,
    ServiceRecordService,
    WarrantyService,
    InvitationService,
    SystemMessageService,
    ApiSenseService,
    OnboardingQuestionsService,
    // Sentry error monitoring
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: 'externalUrlRedirectResolver',
      useValue: navigateExternal,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
